import React from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { OperationsIcon } from 'assets/icons';
import { CategoryCard, Level3Category } from 'components/Page/PageSideBar/PageSideBar.styled';
import StopPropagation from 'components/StopPropagation/StopPropagation';
import { TreeType } from 'components/TreeMenu/TreeMenu';
import { getEasyBasketEnabled } from 'domains/appContext/AppContext.store';
import { ActiveTabParam, PARAM_OPERATIONS, PreferredTabParam } from 'domains/catalog/Catalog.types';
import { formatCategoryLabelToDataCy } from 'domains/catalog/Catalog.utils';
import { Flex, Icon, Text } from 'UI';

interface SubCategoryCardProps {
  level2Label: string;
  level2NodeId: string;
  level1NodeId: string;
  items: TreeType[];
  setActiveCategory: (s: string) => void;
  baseUrl: string;
  disableLevel2Click?: boolean;
  hasOperations: boolean;
}

const SubCategoryCard = ({
  level2Label,
  level2NodeId,
  items,
  level1NodeId,
  setActiveCategory,
  baseUrl,
  disableLevel2Click,
  hasOperations,
}: SubCategoryCardProps) => {
  const history = useHistory();
  const urlParams = new URLSearchParams(history.location.search);
  const isEasyBasketEnabled = useSelector(getEasyBasketEnabled);

  const handleRedirect = (level1: string, level2: string, level3?: string) => {
    if (!level3 && disableLevel2Click) return;
    const link = `${baseUrl}/${level1}/${level2}${level3 ? `/${level3}` : ''}`;
    const linkWithParams = `${link}?${urlParams}`;
    history.push(linkWithParams);
    setActiveCategory('');
  };

  const handleOperationsRedirect = () => {
    const firstLevel3WithOperations = items.find((i) => i.hasOperations);
    if (!firstLevel3WithOperations) {
      return;
    }
    urlParams.set(ActiveTabParam, PARAM_OPERATIONS);
    urlParams.set(PreferredTabParam, PARAM_OPERATIONS);
    handleRedirect(level1NodeId, level2NodeId, firstLevel3WithOperations?.nodeId);
    scrollTo(0, 0);
  };

  return (
    <CategoryCard direction={'column'} gap={15} size={0}>
      <Flex justify={'space-between'} align={'center'} size={0} gap={15}>
        <Text
          type={'h4_paragraph'}
          transform={'uppercase'}
          data-cy={formatCategoryLabelToDataCy(level2Label, 'subtab')}
          cursor={disableLevel2Click ? 'initial' : 'pointer'}
          onClick={() => handleRedirect(level1NodeId, level2NodeId)}
        >
          {level2Label}
        </Text>
        {hasOperations && isEasyBasketEnabled && (
          <StopPropagation>
            <Icon IconComponent={OperationsIcon} size={32} hoverScale onClick={handleOperationsRedirect} />
          </StopPropagation>
        )}
      </Flex>
      <Flex direction={'column'} gap={2} wrap={'wrap'}>
        {items.map((level3) => (
          <Level3Category
            key={level3.nodeId}
            align={'center'}
            onClick={() => handleRedirect(level1NodeId, level2NodeId, level3.nodeId)}
          >
            <Text
              type="base"
              disableGutter
              transform={'capitalize'}
              data-cy={formatCategoryLabelToDataCy(level3.label, 'subtab')}
            >
              {level3.label}
            </Text>
          </Level3Category>
        ))}
      </Flex>
    </CategoryCard>
  );
};

export default SubCategoryCard;
